import { CELL, GROUND } from '../core/config';
import { key, MATERIALS, type Building, type City, type Place } from './city';
import type { Vec } from '../destruction/structure';

export interface Dock {
  id: string;
  name: string;
  building: number;
  gates: Place[];
  slots: { side: number; z: number; edge: number; support: number }[];
  store: Vec;
  stock: number;
  received: number;
  supplyCell: number;
  waiting: Vec[];
}
export interface MarinaLayout {
  docks: Dock[];
}
export const DOCK_TOP = 0.38;

/** Piled, damageable public piers connect to the existing coastal promenade. */
export function prepareMarina(city: City): City {
  if (!city.coast || city.marina) return city;
  city.props ??= [];
  city.places ??= [];
  city.marina = { docks: [] };
  let serial =
    Math.max(
      ...[...city.buildings, ...city.props, ...(city.infrastructure ?? []), ...(city.bridges ?? [])].map(
        (b) => b.id,
      ),
    ) + 1;
  for (const [i, x] of [270, 450].entries()) {
    const b: Building = {
      id: serial++,
      name: i ? 'Seabreeze excursion pier' : 'Linden packet and ferry pier',
      kind: 'paving',
      category: 'prop',
      district: 'coast',
      x,
      z: 655,
      baseY: -8.5,
      exactGeometry: true,
      w: 3,
      d: 27,
      h: 2,
      color: 0xb4a287,
      accent: 0x5f837e,
      cells: [],
    };
    const add = (
      cx: number,
      cy: number,
      cz: number,
      material: 'wood' | 'metal' | 'concrete',
      bottom: number,
      height: number,
      width = 1,
      depth = 1,
      root = false,
      color = b.color,
    ) => {
      const id = b.id * 1e6 + key(cx, cy, cz);
      b.cells.push({
        id,
        x: cx,
        y: cy,
        z: cz,
        material,
        role: cy === 1 ? 'slab' : 'prop',
        color,
        root,
        hp: MATERIALS[material].strength,
        bounds: {
          x: (1 - width) / 2,
          y: (bottom + 8.5) / CELL - cy,
          z: (1 - depth) / 2,
          w: width,
          h: height / CELL,
          d: depth,
        },
      });
      return id;
    };
    for (let z = 0; z < 27; z++)
      for (let xx = 0; xx < 3; xx++) {
        add(xx, 1, z, 'wood', DOCK_TOP - 0.2, 0.2);
        if ((z % 4 === 0 || z === 26) && xx !== 1)
          add(xx, 0, z, 'concrete', -8.5, DOCK_TOP + 8.5 - 0.2, 0.24, 0.24, true, 0x82938d);
        if (z > 4 && (xx === 0 || xx === 2) && z !== 21 && z !== 22 && z !== 23)
          add(xx, 2, z, 'metal', DOCK_TOP, 0.85, 0.08, 0.08, false, 0x637f79);
      }
    city.props.push(b);
    const plaza: Building = {
      id: serial++,
      name: `${b.name} · boarding court`,
      kind: 'paving',
      category: 'prop',
      district: 'coast',
      x: x - 5,
      z: 653.75,
      baseY: GROUND,
      exactGeometry: true,
      w: 12,
      d: 8,
      h: 1,
      color: 0xbcb5a1,
      accent: 0x7a9690,
      cells: [],
    };
    for (let zz = 0; zz < 8; zz++)
      for (let xx = 0; xx < 12; xx++) {
        // Preserve the original pier underneath; do not stack coincident decking.
        if (xx >= 4 && xx < 7 && zz >= 1) continue;
        plaza.cells.push({
          id: plaza.id * 1e6 + key(xx, 0, zz),
          x: xx,
          y: 0,
          z: zz,
          root: true,
          material: 'concrete',
          role: 'slab',
          hp: MATERIALS.concrete.strength,
          color: plaza.color,
          bounds: { x: 0, y: 0, z: 0, w: 1, h: (DOCK_TOP - GROUND) / CELL, d: 1 },
        });
      }
    city.props.push(plaza);
    const slots = [-1, 1].map((side) => ({
      side,
      z: 682.5,
      edge: x + (side < 0 ? 0 : 3 * CELL),
      support: b.id * 1e6 + key(side < 0 ? 0 : 2, 1, 22),
    }));
    const gates = slots.map((slot, s): Place => ({
      id: `dock:${i}:${s}`,
      kind: 'dock',
      name: `${b.name} · berth ${s + 1}`,
      buildingId: b.id,
      floor: 0,
      x: x + 1.9,
      y: DOCK_TOP,
      z: 657.5 + s * 1.5,
      supportCell: b.id * 1e6 + key(1, 1, Math.floor((657.5 + s * 1.5 - 655) / CELL)),
      capacity: 6,
      duration: 0,
    }));
    const supply: Building = {
      id: serial++,
      name: `${b.name} · parcel store`,
      kind: 'furniture',
      furniture: 'crate',
      category: 'prop',
      district: 'coast',
      x: x + 0.3,
      z: 666,
      baseY: DOCK_TOP,
      w: 1,
      d: 2,
      h: 1,
      color: 0xc0a379,
      accent: 0x708c78,
      cells: [],
    };
    for (let z = 0; z < 2; z++)
      supply.cells.push({
        id: supply.id * 1e6 + key(0, 0, z),
        x: 0,
        y: 0,
        z,
        material: 'wood',
        role: 'prop',
        root: false,
        hp: MATERIALS.wood.strength,
        color: 0xc0a379,
        bounds: { x: 0, y: 0, z: 0, w: 0.75, h: 0.6, d: 0.8 },
      });
    supply.parentCell = b.id * 1e6 + key(0, 1, 9);
    city.props.push(supply);
    city.places.push(...gates);
    city.marina.docks.push({
      id: `dock:${i}`,
      name: b.name,
      building: b.id,
      gates,
      slots,
      store: { x: x + 2.5, y: DOCK_TOP, z: 667 },
      stock: i ? 0 : 24,
      received: 0,
      supplyCell: supply.cells[0].id,
      waiting: Array.from({ length: 8 }, (_, k) => ({
        x: x + (k % 2 ? -2.7 : 6.4),
        y: DOCK_TOP,
        z: 655.5 + Math.floor(k / 2) * 2.1,
      })),
    });
    city.coast.paths.push({
      id: `pier-access:${i}`,
      width: 3.75,
      points: [
        { x: x + (i ? 5.625 : 2.5), y: GROUND, z: 639.375 },
        ...(i ? [{ x: x + 5.625, y: GROUND, z: 652.5 }] : []),
        { x: x + 2.5, y: DOCK_TOP, z: 656.25 },
        { x: x + 2.5, y: DOCK_TOP, z: 659.375 },
        { x: x + 2.5, y: DOCK_TOP, z: 682.5 },
        { x: x + 0.5, y: DOCK_TOP, z: 682.5 },
      ],
    });
  }
  return city;
}
